import React, { useState, useEffect, useCallback } from "react";

const LightPuzzleGame = () => {
  const [numLights, setNumLights] = useState(2);
  const [lights, setLights] = useState([false, false]);
  const [mode, setMode] = useState("normal"); // 'normal', 'devil', 'edit'
  const [clickCount, setClickCount] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [clickedButton, setClickedButton] = useState(null);
  const [devilStates, setDevilStates] = useState(new Set());
  const [initialState, setInitialState] = useState([false, false]);
  const [operationHistory, setOperationHistory] = useState([]);

  // 生成所有可能的状态（除了全亮状态）
  const generateAllStatesExceptAllOn = useCallback((numLights) => {
    const states = new Set();
    const totalStates = Math.pow(2, numLights);
    const allOnState = totalStates - 1; // 全亮状态的二进制表示

    for (let i = 0; i < totalStates; i++) {
      if (i !== allOnState) {
        states.add(i);
      }
    }
    return states;
  }, []);

  // 将状态数字转换为布尔数组
  const stateToArray = useCallback((state, numLights) => {
    const arr = [];
    for (let i = 0; i < numLights; i++) {
      arr.push((state & (1 << i)) !== 0);
    }
    return arr;
  }, []);

  // 将布尔数组转换为状态数字
  const arrayToState = useCallback((arr) => {
    let state = 0;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i]) {
        state |= 1 << i;
      }
    }
    return state;
  }, []);

  // 应用开关操作（开关i控制灯i）
  const applySwitch = useCallback((lightState, switchIndex) => {
    const newState = [...lightState];
    newState[switchIndex] = !newState[switchIndex];
    return newState;
  }, []);

  // 检查是否所有灯都亮
  const checkAllLightsOn = useCallback((lightState) => {
    return lightState.every((light) => light);
  }, []);

  // 初始化游戏
  const initializeGame = useCallback(() => {
    setOperationHistory([]);

    if (mode === "normal") {
      // 普通模式：随机生成初始状态
      const randomState = [];
      for (let i = 0; i < numLights; i++) {
        randomState.push(Math.random() < 0.5);
      }
      // 确保不是全亮状态
      if (checkAllLightsOn(randomState)) {
        randomState[0] = false;
      }
      setLights(randomState);
      setInitialState([...randomState]);
      setDevilStates(new Set());
    } else if (mode === "devil") {
      // 恶意模式：从所有非全亮状态开始
      const allStates = generateAllStatesExceptAllOn(numLights);
      setDevilStates(new Set(allStates));

      // 初始状态不确定，设置为全灭（反正看不到）
      setLights(new Array(numLights).fill(false));
      setInitialState(new Array(numLights).fill(false)); // 临时设置，最后会重新计算
    }

    setClickCount(0);
    setIsSuccess(false);
    setClickedButton(null);
  }, [mode, numLights, generateAllStatesExceptAllOn, checkAllLightsOn]);

  // 处理按钮点击
  const handleSwitchClick = (switchIndex) => {
    if (isSuccess) return;

    setClickedButton(switchIndex);
    setTimeout(() => setClickedButton(null), 200);

    if (mode === "devil") {
      // 恶意模式逻辑
      const currentStates = new Set(devilStates);
      const nextStates = new Set();

      // 对每个当前可能状态，模拟点击操作
      for (const state of currentStates) {
        const stateArray = stateToArray(state, numLights);
        const afterSwitch = applySwitch(stateArray, switchIndex);

        // 如果点击后不会成功（不是全亮），则记录新状态
        if (!checkAllLightsOn(afterSwitch)) {
          const newStateNum = arrayToState(afterSwitch);
          nextStates.add(newStateNum);
        }
        // 如果点击后会成功，就排除这个状态（什么也不做）
      }

      setDevilStates(nextStates);

      // 如果没有剩余状态了，说明玩家成功了
      if (nextStates.size === 0) {
        // 重新构建恶意模式的完整操作历史
        const clickHistory = [...operationHistory, switchIndex];
        reconstructDevilHistory(clickHistory);
        setLights(new Array(numLights).fill(true));
        setIsSuccess(true);
      } else {
        // 记录这次点击
        setOperationHistory((prev) => [...prev, switchIndex]);
      }
    } else {
      // 普通模式
      const oldLights = [...lights];
      const newLights = applySwitch(lights, switchIndex);

      // 记录操作历史
      setOperationHistory((prev) => [
        ...prev,
        {
          buttonIndex: switchIndex,
          beforeState: [...oldLights],
          afterState: [...newLights],
        },
      ]);

      setLights(newLights);
      if (checkAllLightsOn(newLights)) {
        setIsSuccess(true);
      }
    }

    setClickCount((prev) => prev + 1);
  };

  // 重新构建恶意模式的操作历史
  const reconstructDevilHistory = (clickHistory) => {
    // 找到一个能够通过这些操作达到成功的初始状态
    const allInitialStates = generateAllStatesExceptAllOn(numLights);

    // 先尝试找一个不会中途成功的路径
    for (const initialStateNum of allInitialStates) {
      let currentState = stateToArray(initialStateNum, numLights);
      const history = [];
      let isValidPath = true;

      // 模拟每一步操作
      for (let i = 0; i < clickHistory.length; i++) {
        const buttonIndex = clickHistory[i];
        const beforeState = [...currentState];
        currentState = applySwitch(currentState, buttonIndex);

        history.push({
          buttonIndex,
          beforeState,
          afterState: [...currentState],
        });

        // 检查中间步骤是否提前成功（对于恶意模式，除了最后一步，中间不应该成功）
        if (i < clickHistory.length - 1 && checkAllLightsOn(currentState)) {
          isValidPath = false;
          break;
        }
      }

      // 如果最后一步成功了，且中间没有提前成功，这就是我们要的路径
      if (isValidPath && checkAllLightsOn(currentState)) {
        setInitialState(stateToArray(initialStateNum, numLights));
        setOperationHistory(history);
        return;
      }
    }

    // 如果没找到完美路径，就用第一个能成功的路径
    for (const initialStateNum of allInitialStates) {
      let currentState = stateToArray(initialStateNum, numLights);
      const history = [];

      // 模拟每一步操作
      for (const buttonIndex of clickHistory) {
        const beforeState = [...currentState];
        currentState = applySwitch(currentState, buttonIndex);

        history.push({
          buttonIndex,
          beforeState,
          afterState: [...currentState],
        });
      }

      // 如果最后成功了，就用这个路径
      if (checkAllLightsOn(currentState)) {
        setInitialState(stateToArray(initialStateNum, numLights));
        setOperationHistory(history);
        return;
      }
    }

    // 如果还是没有找到，创建一个默认的路径（这种情况理论上不应该发生）
    const defaultInitial = new Array(numLights).fill(false);
    let currentState = [...defaultInitial];
    const history = [];

    for (const buttonIndex of clickHistory) {
      const beforeState = [...currentState];
      currentState = applySwitch(currentState, buttonIndex);

      history.push({
        buttonIndex,
        beforeState,
        afterState: [...currentState],
      });
    }

    setInitialState(defaultInitial);
    setOperationHistory(history);
  };

  // 改变灯的数量
  const changeNumLights = (newNum) => {
    if (newNum < 1 || newNum > 10) return;

    setNumLights(newNum);
    setLights(new Array(newNum).fill(false));
    setInitialState(new Array(newNum).fill(false));
    setOperationHistory([]);
  };

  useEffect(() => {
    if (mode !== "edit") {
      initializeGame();
    }
  }, [mode, numLights, initializeGame]);

  const renderEditMode = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-center">编辑模式</h2>

      <div className="flex items-center justify-center gap-4">
        <label className="text-lg">灯和开关数量:</label>
        <input
          type="number"
          min="1"
          max="10"
          value={numLights}
          onChange={(e) => changeNumLights(parseInt(e.target.value))}
          className="w-16 px-2 py-1 border rounded text-center"
        />
      </div>

      <div className="text-center text-gray-600">
        <p>
          默认配置：开关{" "}
          {Array.from({ length: numLights }, (_, i) => i + 1).join("、")}{" "}
          分别控制灯{" "}
          {Array.from({ length: numLights }, (_, i) => i + 1).join("、")}
        </p>
      </div>

      <div className="flex justify-center gap-4">
        <button
          onClick={() => setMode("normal")}
          className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          开始普通模式
        </button>
        <button
          onClick={() => setMode("devil")}
          className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          开始恶意模式
        </button>
      </div>
    </div>
  );

  const renderGameMode = () => (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-center">Q10-1. 点亮所有灯</h1>

      {/* 模式选择 */}
      <div className="flex justify-center gap-4">
        <button
          onClick={() => setMode("normal")}
          className={`px-4 py-2 rounded ${
            mode === "normal" ? "bg-blue-500 text-white" : "bg-gray-200"
          }`}
        >
          普通模式
        </button>
        <button
          onClick={() => setMode("devil")}
          className={`px-4 py-2 rounded ${
            mode === "devil" ? "bg-red-500 text-white" : "bg-gray-200"
          }`}
        >
          恶意模式
        </button>
        <button
          onClick={() => setMode("edit")}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          编辑设置
        </button>
      </div>

      {/* 当前模式说明 */}
      <div className="text-center text-sm text-gray-600">
        {mode === "normal" && "普通模式：随机初始状态，正常游戏"}
        {mode === "devil" && "恶意模式：故意让你失败，直到最后一刻"}
      </div>

      {/* 灯的显示区域 */}
      <div className="flex justify-center gap-4">
        {Array.from({ length: numLights }, (_, index) => (
          <div key={index} className="relative">
            {/* 背景灯泡 */}
            <div
              className={`w-20 h-20 rounded-full border-4 flex items-center justify-center text-lg font-bold transition-all ${
                isSuccess
                  ? "bg-yellow-400 border-yellow-600 text-yellow-800 shadow-lg shadow-yellow-300"
                  : "bg-gray-300 border-gray-500 text-gray-600"
              }`}
            >
              {index + 1}
            </div>

            {/* 迷雾效果 */}
            {!isSuccess && (
              <div className="absolute inset-0 bg-gray-400 bg-opacity-70 rounded-full flex items-center justify-center">
                <div className="w-16 h-16 bg-gray-500 bg-opacity-50 rounded-full animate-pulse">
                  <div className="w-full h-full bg-gradient-radial from-white from-10% via-gray-300 via-50% to-gray-500 rounded-full opacity-60"></div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 开关按钮 */}
      <div className="flex justify-center gap-6">
        {Array.from({ length: numLights }, (_, index) => (
          <button
            key={index}
            onClick={() => handleSwitchClick(index)}
            className={`w-16 h-16 rounded-full font-bold text-lg transition-all shadow-lg ${
              clickedButton === index
                ? "bg-blue-700 text-white scale-90 shadow-blue-500/50"
                : "bg-blue-500 text-white hover:bg-blue-600 hover:scale-105 shadow-blue-400/50"
            } ${isSuccess ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={isSuccess}
          >
            {index + 1}
          </button>
        ))}
      </div>

      {/* 游戏状态 */}
      <div className="text-center space-y-2">
        <div className="text-lg">
          按钮点击次数:{" "}
          <span className="font-bold text-blue-600">{clickCount}</span>
        </div>

        {mode === "devil" && devilStates.size > 0 && !isSuccess && (
          <div className="text-sm text-red-600">
            恶意模式剩余可能状态: {devilStates.size}
          </div>
        )}

        {isSuccess && (
          <div className="text-2xl font-bold text-green-600 animate-bounce">
            🎉 成功点亮所有灯！🎉
          </div>
        )}
      </div>

      {/* 操作历史记录 */}
      {isSuccess && operationHistory && operationHistory.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-bold text-center mb-4">操作过程记录</h3>

          {/* 初始状态 */}
          {initialState && (
            <div className="mb-4">
              <div className="font-semibold text-sm text-gray-700 mb-2">
                初始状态：
              </div>
              <div className="flex justify-center gap-2">
                {initialState.map((isOn, index) => (
                  <div
                    key={index}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                      isOn
                        ? "bg-yellow-300 border-yellow-500 text-yellow-800"
                        : "bg-gray-200 border-gray-400 text-gray-600"
                    }`}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 每次操作 */}
          <div className="space-y-3">
            {operationHistory.map((operation, index) => {
              // 确保操作对象有正确的结构
              if (
                !operation ||
                typeof operation.buttonIndex === "undefined" ||
                !operation.beforeState ||
                !operation.afterState
              ) {
                return null;
              }

              return (
                <div key={index} className="border-l-4 border-blue-300 pl-4">
                  <div className="font-semibold text-sm text-gray-700">
                    第 {index + 1} 步：点击开关 {operation.buttonIndex + 1}
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    {/* 操作前状态 */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">操作前:</span>
                      <div className="flex gap-1">
                        {operation.beforeState.map((isOn, lightIndex) => (
                          <div
                            key={lightIndex}
                            className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs ${
                              isOn
                                ? "bg-yellow-300 border-yellow-500 text-yellow-800"
                                : "bg-gray-200 border-gray-400 text-gray-600"
                            }`}
                          >
                            {lightIndex + 1}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 箭头 */}
                    <div className="text-blue-500 font-bold">→</div>

                    {/* 操作后状态 */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">操作后:</span>
                      <div className="flex gap-1">
                        {operation.afterState.map((isOn, lightIndex) => (
                          <div
                            key={lightIndex}
                            className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs ${
                              isOn
                                ? "bg-yellow-300 border-yellow-500 text-yellow-800"
                                : "bg-gray-200 border-gray-400 text-gray-600"
                            } ${
                              operation.beforeState[lightIndex] !== isOn
                                ? "ring-2 ring-red-400"
                                : ""
                            }`}
                          >
                            {lightIndex + 1}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {mode === "devil" && (
            <div className="mt-4 p-2 bg-red-100 rounded text-sm text-red-700">
              <strong>恶意模式说明：</strong>
              以上显示的是在恶意模式下，系统最终选定的一种可能路径。
              实际游戏过程中，系统会排除所有能提前成功的状态，只保留让你"最倒霉"的情况。
            </div>
          )}
        </div>
      )}

      {/* 重新开始按钮 */}
      <div className="flex justify-center">
        <button
          onClick={initializeGame}
          className="px-6 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
        >
          重新开始
        </button>
      </div>

      {/* 题目说明 */}
      <div className="mt-8 p-4 bg-gray-100 rounded-lg text-sm text-gray-700">
        <p className="font-semibold mb-2">游戏规则：</p>
        <p>
          • 有 {numLights} 个开关，分别控制对应的 {numLights} 个灯
        </p>
        <p>• 每次按下某个开关，就会切换对应灯的状态（亮→灭，灭→亮）</p>
        <p>
          • 只有当<strong>所有灯都点亮时</strong>，指示器才会显示"成功点亮"
        </p>
        <p>• 平时你看不到单独的灯是亮还是灭</p>
        <p className="mt-2 text-red-600">
          <strong>恶意模式：</strong>
          系统会故意选择让你失败的状态，直到你找到必胜策略！
        </p>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white min-h-screen">
      {mode === "edit" ? renderEditMode() : renderGameMode()}

      <style jsx>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default LightPuzzleGame;
